import React, { useState } from "react";
import { useQuery } from "react-query";
import { getApiVersion, ApiVersionInfo } from "../helpers/api/version";

// VERSION-SURFACING.md §4.3 — full modal with portal + API blocks
// side-by-side, plus a one-line "copy to clipboard" digest for support
// tickets.

interface Props {
  onClose: () => void;
}

const PORTAL_NAME = "concierge-admin-fe";

const FE = {
  version: process.env.REACT_APP_VERSION ?? "0.0.0",
  sha: process.env.REACT_APP_GIT_SHA ?? "dev",
  branch: process.env.REACT_APP_GIT_BRANCH ?? "local",
  buildTime: process.env.REACT_APP_BUILD_TIME ?? "unknown",
  envTier: process.env.REACT_APP_ENV_TIER ?? "d1",
};

const fmtTime = (iso: string): string => {
  if (!iso || iso === "unknown") return "unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
};

const fmtUptime = (s?: number): string => {
  if (typeof s !== "number" || s < 0) return "?";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const VersionModal: React.FC<Props> = ({ onClose }) => {
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError } = useQuery(
    ["api-version"],
    () => getApiVersion(),
    {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: 1,
    }
  );

  const api: ApiVersionInfo | undefined = data?.data;

  const digest = api
    ? `${PORTAL_NAME} v${FE.version}/${FE.sha} → ${api.service} v${api.version}/${api.commit} · ${api.env_tier} · ${new Date().toISOString()}`
    : `${PORTAL_NAME} v${FE.version}/${FE.sha} · ${FE.envTier} · ${new Date().toISOString()}`;

  const copyDigest = async () => {
    try {
      await navigator.clipboard.writeText(digest);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: 480,
          maxWidth: "92vw",
          background: "#fff",
          borderRadius: 6,
          boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
          fontSize: 13,
          fontFamily: "system-ui, -apple-system, sans-serif",
          color: "#0a0a0a",
        }}
      >
        <div
          style={{
            padding: "1rem 1.25rem",
            borderBottom: "1px solid #f0efee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong style={{ fontSize: 15 }}>Build info</strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              fontSize: 18,
              cursor: "pointer",
              color: "#737373",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "1rem 1.25rem" }}>
          <SectionTitle>Portal</SectionTitle>
          <KV label={PORTAL_NAME} value={`v${FE.version}`} />
          <KV label="commit" value={FE.sha} mono />
          <KV label="branch" value={FE.branch} mono />
          <KV label="built" value={fmtTime(FE.buildTime)} />
          <KV label="env" value={FE.envTier} mono />

          <div style={{ marginTop: "1rem" }} />
          <SectionTitle>API</SectionTitle>
          {isLoading && <Muted>Loading…</Muted>}
          {isError && <Muted>Could not reach /api/version.</Muted>}
          {api && (
            <>
              <KV label={api.service} value={`v${api.version}`} />
              <KV label="commit" value={api.commit} mono />
              <KV label="branch" value={api.branch} mono />
              <KV label="built" value={fmtTime(api.build_time)} />
              <KV label="env" value={api.env_tier} mono />
              {api.node_version && <KV label="node" value={api.node_version} mono />}
              <KV label="uptime" value={fmtUptime(api.uptime_seconds)} />
            </>
          )}
        </div>

        <div
          style={{
            padding: "0.75rem 1.25rem",
            borderTop: "1px solid #f0efee",
            display: "flex",
            justifyContent: "space-between",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={copyDigest}
            style={{
              border: "1px solid #dfdfdf",
              borderRadius: 4,
              background: "#fff",
              padding: "0.375rem 0.75rem",
              fontSize: 12,
              cursor: "pointer",
              color: "#0a0a0a",
            }}
          >
            {copied ? "Copied!" : "Copy to clipboard"}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid #1b3a34",
              borderRadius: 4,
              background: "#1b3a34",
              color: "#fff",
              padding: "0.375rem 0.875rem",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "#737373",
      margin: "0 0 0.5rem",
    }}
  >
    {children}
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "2px 0",
      fontSize: 13,
    }}
  >
    <span style={{ color: "#737373" }}>{label}</span>
    <span style={{ fontFamily: mono ? "ui-monospace, monospace" : "inherit" }}>{value}</span>
  </div>
);

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ color: "#a3a3a3", fontSize: 12 }}>{children}</div>
);

export default VersionModal;
